// Login page. The ONLY public route in the app.

import { useState } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { signInWithGoogle, WORKSPACE_DOMAIN } from '@/lib/auth';
import { useAuth } from '@/hooks/useAuth';

interface LoginPageProps {
  reason?: string;
}

export default function LoginPage({ reason }: LoginPageProps): JSX.Element {
  const navigate = useNavigate();
  const { user, isAuthorized, loading } = useAuth();
  const [error, setError] = useState<string | null>(
    reasonToMessage(reason),
  );
  const [busy, setBusy] = useState(false);

  // Already signed in? Bounce to /orders.
  useEffect(() => {
    if (!loading && user && isAuthorized) {
      void navigate({ to: '/orders' });
    }
  }, [loading, user, isAuthorized, navigate]);

  async function handleSignIn(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      const user = await signInWithGoogle();
      if (!user.email || !user.email.toLowerCase().endsWith(`@${WORKSPACE_DOMAIN}`)) {
        setError(`Domain not allowed. Use an @${WORKSPACE_DOMAIN} account.`);
        return;
      }
      // AuthGate will pick up the state change and navigate.
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Sign-in failed.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-full flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-[420px] p-8">
        <p className="card-eyebrow">GoodSweat</p>
        <h1
          className="font-display font-semibold mb-2"
          style={{ fontSize: 32, lineHeight: '40px', letterSpacing: '-0.015em' }}
        >
          Admin sign-in
        </h1>
        <p
          className="text-[var(--color-fg-muted)] mb-6"
          style={{ fontSize: 15, lineHeight: '22px' }}
        >
          Workspace accounts only —{' '}
          <code className="font-mono text-caption">@{WORKSPACE_DOMAIN}</code>.
        </p>

        <Button
          variant="primary"
          onClick={handleSignIn}
          disabled={busy}
          className="w-full"
        >
          {busy ? 'Opening Google…' : 'Sign in with Google'}
        </Button>

        {error ? (
          <p
            className="mt-4 rounded-md px-3 py-2 text-[13px]"
            style={{
              background: 'rgba(239, 117, 107, 0.12)',
              color: 'var(--color-coral-600)',
              border: '1px solid rgba(239, 117, 107, 0.24)',
            }}
            role="alert"
          >
            {error}
          </p>
        ) : null}

        <p
          className="mt-8 text-[var(--color-fg-subtle)]"
          style={{ fontSize: 11, lineHeight: '14px', letterSpacing: '0.04em' }}
        >
          Sign-in is gated server-side by an allow-list and the workspace
          domain. Your Google session is used only to issue an ID token.
        </p>
      </Card>
    </div>
  );
}

function reasonToMessage(reason: string | undefined): string | null {
  switch (reason) {
    case 'wrong_domain':
      return 'Domain not allowed. Use a commonequity.com account.';
    case 'session_expired':
      return 'Your session expired. Sign in again to continue.';
    default:
      return null;
  }
}

/** Helper to read `?reason=` off the URL without pulling a search params lib. */
export function useLoginReason(): string | undefined {
  const search = useRouterState({ select: (s) => s.location.search });
  if (typeof search === 'object' && search) {
    const r = (search as { reason?: string }).reason;
    return typeof r === 'string' ? r : undefined;
  }
  return undefined;
}
